/**
 * Public token sign page (Phase Conv-2).
 *
 *  /sign/:token
 *
 * Per-party, single-use sign link.  Loads the document context, lets the
 * party download the draft PDF, and confirms their signature by typing
 * their full name (matched soft-case against the party.name on the
 * sign-request).  Idempotent — re-visiting after signing simply shows the
 * confirmation state.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, FileText, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sign/${token}`)
      .then((res) => {
        setData(res.data);
        setSigned(res.data?.party?.status === "signed"
          || res.data?.request?.status === "fully_signed");
      })
      .catch((e) => setError(e?.response?.data?.detail || "Invalid or expired signing link"))
      .finally(() => setLoading(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await axios.post(`${API}/sign/${token}`, { confirm_name: confirmName });
      setSigned(true);
      // Refresh
      const r2 = await axios.get(`${API}/sign/${token}`);
      setData(r2.data);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Could not record signature");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-zinc-500 text-sm flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your signing link…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6">
        <div className="bg-white border border-zinc-200 max-w-md w-full p-8 text-center" data-testid="sign-error">
          <AlertCircle className="h-10 w-10 text-red-600 mx-auto mb-3" />
          <div className="text-lg font-medium text-zinc-900 mb-1">Link not valid</div>
          <div className="text-sm text-zinc-600">{error}</div>
          <div className="text-xs text-zinc-400 mt-4">
            If you believe this is in error, contact E Holtzhausen INC on estelle@ehinc.co.za.
          </div>
        </div>
      </div>
    );
  }

  const { request, party, document, matter, firm } = data || {};

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Firm header */}
      <header className="bg-zinc-900 text-white px-6 py-5">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <ShieldCheck className="h-5 w-5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">{firm?.name}</div>
            <div className="text-sm font-medium">Secure document signing portal</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6" data-testid="sign-page">
        {/* Doc summary */}
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <FileText className="h-6 w-6 text-zinc-400 shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Document for signature</div>
              <div className="text-xl font-medium text-zinc-900 break-words" data-testid="sign-doc-name">
                {document?.template_name || document?.template_key}
              </div>
              {matter?.property && (
                <div className="text-sm text-zinc-600 mt-1">Property: {matter.property}</div>
              )}
              <div className="text-xs text-zinc-500 mt-2">
                Sent to: <span className="font-medium text-zinc-700">{party?.name}</span> · {party?.role}
              </div>
            </div>
            <a
              href={`${API}/sign/${token}/pdf`}
              target="_blank" rel="noreferrer"
              className="text-xs px-3 py-1.5 border border-zinc-300 hover:bg-zinc-50"
              data-testid="sign-download-pdf"
            >
              View PDF draft →
            </a>
          </div>
        </div>

        {/* Sign state */}
        {signed ? (
          <div className="bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-4" data-testid="sign-confirmed">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-0.5" />
            <div>
              <div className="text-lg font-medium text-emerald-900">Signature recorded</div>
              <div className="text-sm text-emerald-800 mt-1">
                Thank you, {party?.name}. Your signature has been recorded for this document.
              </div>
              <div className="text-[11px] text-emerald-700 mt-2">
                Status: <span className="font-medium">{request?.status?.replace("_", " ")}</span>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="bg-white border border-zinc-200 p-6 space-y-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Confirm signature</div>
            <div className="text-sm text-zinc-700">
              By typing your full name below and clicking <span className="font-medium">Sign now</span>, you
              confirm that:
            </div>
            <ul className="text-xs text-zinc-600 list-disc pl-5 space-y-1">
              <li>You have read and understood the contents of the document above;</li>
              <li>You are <span className="font-medium">{party?.name}</span> ({party?.role});</li>
              <li>Your typed name constitutes your electronic signature in terms of the Electronic Communications and Transactions Act 25 of 2002.</li>
            </ul>
            <label className="block">
              <span className="text-xs text-zinc-500">Type your full name to sign</span>
              <Input
                type="text"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={party?.name}
                className="mt-1 rounded-none"
                data-testid="sign-name-input"
                required
              />
            </label>
            {submitError && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="sign-error-msg">
                {submitError}
              </div>
            )}
            <Button type="submit" disabled={submitting || !confirmName.trim()}
                    className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white"
                    data-testid="sign-submit-btn">
              {submitting ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Recording…</>) : "Sign now"}
            </Button>
          </form>
        )}

        <div className="text-[11px] text-zinc-400 text-center pt-2">
          Need help? Contact {firm?.email} · {firm?.phone}
        </div>
      </main>
    </div>
  );
}
